/**
 * The detail pane: what is selected, what was decoded, and **which of the two it is** (P9.5, P9.6).
 *
 * One rule runs through every method here. **A stored sequence and a generated one are never
 * shown the same way**, and a reconstruction never replaces the sequence it reconstructs — it
 * appears beside it, labelled (P9.6). The map's whole claim in inspect mode is that everything on
 * screen was really in the corpus at a position that is really its own (`V14`); a pane that let a
 * decoded string sit where a stored one had been would break that claim silently.
 *
 * **Every decoded sequence names the fill and the sampler that produced it** (§3.5). Two sequences
 * from the same coordinate can differ for either reason, and a pane naming neither would make the
 * difference unattributable.
 */
import type { DecodeResponse } from '../api';
import type { DecodedMark, InterpolationRun } from '../decoded';
import type { EncodedMark } from '../encoded';
import type { PointDatum } from '../renderer';
import { atLabel, stepLabel, type Step } from '../trajectory';
import type { RunPosition } from '../interpolation';
import { residueSpans } from './residues';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const child of children) node.append(child);
  return node;
}

/**
 * A card's label line: what the point is, and where it sits in the walk (`V134`).
 *
 * **Plain text, and the highlighter is not applied to it.** The reader's decision, 2026-08-28: only
 * the residues that differ are highlighted. `V132` briefly wrapped the walk half in a `.walk` span
 * and washed it yellow, because `--diff` had become a background rather than a stroke and a wash
 * needs an extent to sit under. That was a channel nobody asked for, and it put a yellow band across
 * a line whose job is to name a step in words.
 *
 * So one text node, joined by `·` like every other label in this file. Omitted `step` gives exactly
 * the one text node this was before Phase 27, which is what keeps a card pinned in plain inspect
 * mode unchanged.
 */
const labelLine = (origin: string, step?: Step): string =>
  step === undefined ? origin : [origin, ...stepLabel(step)].join(' · ');

/** Columns the pane never lists: they are addresses, not properties of the peptide (`V51`). */
const NOT_A_PROPERTY = new Set(['x', 'y', 'ix', 'row', 'sequence']);

export interface Detail {
  showSelection(datum: PointDatum, onReconstruct: () => void, step?: Step): void;
  showReconstruction(result: DecodeResponse): void;
  showDecoded(result: DecodeResponse, xy: [number, number], step?: Step): void;
  /** A sequence the reader typed, and where this model put it (`V137`, P29.4). */
  showEncoded(mark: EncodedMark, step?: Step): void;
  showError(message: string): void;
  busy(what: string): void;
  clear(): void;
}

/**
 * What `exact=false` means, **which depends on the fill that produced it** (`V109`).
 *
 * `exact` answers one question — were the clicked coordinates reproduced — and the three fills
 * fail it for three different reasons. The pane used to print the `nearest` reason for all of
 * them, so a UMAP inverse (`neighbour_row: null`, no neighbour consulted anywhere) reported
 * "the coordinates are the neighbour’s". That sentence named a row that does not exist.
 *
 * | fill | `exact` | why |
 * |---|---|---|
 * | `hybrid` | true | the two visible coordinates are written into `z` unchanged |
 * | `inverse` under PCA / raw dims | true | the inverse is a linear solve and round-trips |
 * | `inverse` under UMAP | **false** | `umap-learn`'s inverse is an approximate pre-image; `transform(z)` lands near the click, not on it |
 * | `nearest` | **false** | `z` is a stored row's own latent, and it can be arbitrarily far from the click |
 * | `row` / `z` | true | nothing was clicked, so there was no coordinate to reproduce |
 */
function exactness(result: DecodeResponse): string {
  if (result.exact) return 'yes — the clicked coordinates were reproduced';
  if (result.fill === 'nearest') return 'no — the coordinates are the neighbour’s, not the click’s';
  if (result.fill === 'inverse') {
    return 'no — this projection’s inverse is approximate; re-projecting the vector lands near the click, not on it';
  }
  return 'no — the vector does not re-project onto the clicked coordinates';
}

/**
 * The switch that takes the grey notes off a card, leaving the sequence and its highlighting.
 *
 * **A card, not a page setting.** The notes are what a card says *about* a point — where it was
 * placed, what its provenance is, its step in the walk, its distance from the nearest row — and a
 * reader comparing two sequences side by side wants them off *those two* while the rest of an
 * arranged map keeps saying what it is. The state is the caller's to hold, for the reason every
 * other card property is: these builders are pure, and the node is rebuilt rather than mutated.
 *
 * **Never the sequence, and never the close control.** What this hides is exactly `p.label`, which
 * is the muted text and nothing else — the residue spans, their trajectory highlighting and the
 * structure-card affordance on each code are what the card is for.
 */
export interface CardNotes {
  /** `true` when the notes are hidden right now. Absent notes means the button is not drawn. */
  readonly terse: boolean;
  toggle(): void;
}

/** The notes switch itself, or nothing when the caller holds no state for it. */
function notesButton(notes: CardNotes | undefined): HTMLElement[] {
  if (notes === undefined) return [];
  const button = el(
    'button',
    {
      class: 'notes',
      type: 'button',
      'aria-pressed': notes.terse ? 'true' : 'false',
      title: notes.terse ? 'show this card\u2019s notes' : 'hide this card\u2019s notes, keeping the sequence',
    },
    // **A minus and a plus, not two shades of ellipsis**: what the control does is take text away
    // and put it back, and the two states have to be told apart at a glance beside the close.
    notes.terse ? '+' : '\u2212',
  );
  button.addEventListener('click', (event) => {
    event.stopPropagation();
    notes.toggle();
  });
  return [button];
}

/**
 * The card that stays on the map at the point that was clicked (`V112`, `P19.6`).
 *
 * **Not a duplicate of the detail pane, and the difference is what it is for.** deepscatter's
 * hover tooltip already shows the row and the sequence, and it is gone the moment the pointer
 * moves — so the residue codes in it can never be hovered for a structure, which is the whole
 * point of `P18.1`'s spans. This is that tooltip made persistent and interactive: the sequence,
 * as spans, sitting at its own point, until the point is clicked again or the card is closed.
 *
 * Deliberately short. Everything else about the row — its properties, its reconstruction — stays
 * in the pane, which has room for it and does not sit on top of the map.
 *
 * The stored style, because that is exactly what this is: a sequence that was really in the
 * corpus at a position that is really its own (`V14`). A generated one never appears here.
 *
 * **Many of these are on the map at once** (`V116`), one per clicked row, so the close control is
 * the only way each one goes away individually. `onClose` is given the row it belongs to by the
 * caller; this function does not know how many siblings it has.
 *
 * ## `step` is what makes this card part of a trajectory (`V131`, P27)
 *
 * Given, the label names the point's place in the walk and how much of it differs from its
 * baseline, and the differing residues are marked. Omitted, the card is byte-for-byte what it was
 * before Phase 27 — which is what a card pinned in plain inspect mode still is, and what a card
 * becomes again the moment its point is removed from the trajectory.
 *
 * **The border stays `--stored` and that is deliberate.** It says "this is a stored sequence"
 * (P9.5); repurposing it to say "trajectory member" would blur the one distinction the stylesheet
 * is not allowed to blur. The step label and the marked residues are what say it instead.
 */
export function pinnedPointCard(
  datum: PointDatum,
  onClose: () => void,
  step?: Step,
  notes?: CardNotes,
): HTMLElement {
  const close = el(
    'button',
    { class: 'close', type: 'button', title: 'close this card (Escape clears all)' },
    '\u00d7',
  );
  close.addEventListener('click', (event) => {
    event.stopPropagation();
    onClose();
  });
  const node = el(
    'div',
    // `data-drag` is the caller's half of `V128`: the renderer moves whichever element carries it,
    // and knows nothing else about the node. Here that is the card itself.
    { class: 'pinned-point', 'data-drag': '', title: 'drag to reposition' },
    el('p', { class: 'label' }, labelLine(`row ${datum.row}`, step)),
    residueSpans(datum.sequence, 'sequence stored', step?.summary?.positions),
    close,
    ...notesButton(notes),
  );
  if (step !== undefined) node.classList.add('in-trajectory');
  if (notes?.terse === true) node.classList.add('terse');
  // **The card sits inside the map's own element, so its clicks bubble to the map's handler.**
  // Without this, clicking a residue on the card runs the hit test at that pixel and selects
  // whatever point is behind it — which is to say, clicking the card moves the card.
  node.addEventListener('click', (event) => event.stopPropagation());
  return node;
}

/**
 * A decoded point on the map: a diamond, and optionally its card (`V123`, P20.3).
 *
 * ## This is the one place the map shows something that was never in the corpus
 *
 * `V14`'s claim is that everything drawn is a real corpus member at a position that is really its
 * own, and every other surface in this app keeps it: the tooltip, the pinned card and the
 * landmarks are all corpus rows. A decoded point is not, so the marker has to be unmistakable
 * rather than merely different.
 *
 * **A diamond, and the shape is doing the work rather than the colour.** The corpus colour channel
 * is reader-selectable over any categorical or continuous column, so no colour is reliably unused
 * — a round dot in `--generated` is a corpus point under an unlucky palette, and a reader who
 * changed the channel five minutes ago has no way to tell. A rotated square is not a shape
 * deepscatter ever draws. The colour is `--generated` on top of that, and the label says the word.
 *
 * ## The card is the pinned card's twin, in the other half of the palette
 *
 * `pinnedPointCard` exists because deepscatter's hover tooltip cannot be hovered, so its residue
 * codes can never open a structure (`V112`, `P18.1`). Exactly the same is true of a decoded
 * sequence, and "what is this monomer" does not become a different question because the model
 * wrote the sequence instead of reading it. So: the same spans, the same shared residue delegate,
 * the same close control — and `sequence generated` rather than `sequence stored`, which is the
 * distinction `P9.5` will not let this file blur.
 *
 * The provenance — fill, sampler, exactness, the norm — is deliberately **not** here. It is four
 * rows of a definition list that belong in the pane, which has the room; `showDecoded` prints it,
 * and clicking the diamond is what opens it.
 *
 * ## What each gesture is wired to
 *
 * | Gesture | Effect | Why here |
 * |---|---|---|
 * | left click the diamond | `onOpen` | re-opens the **stored** response in the pane; nothing is re-decoded |
 * | right click the diamond | `onDelete` | as asked. `preventDefault`, so the browser menu does not also open |
 * | right click the **card** | nothing — the browser menu opens | the card holds a sequence worth copying, and taking the menu away there would be hostile |
 * | the card's close control | `onCloseCard` | closes the card and leaves the diamond, which is the point of having two elements |
 * | any click inside | `stopPropagation` | without it the click reaches the map handler and, in decoder mode, triggers a **second decode** at that pixel — the bug already fixed for `pinnedPointCard` |
 *
 * ## `step`, and why a diamond can be a step of a walk (`V132`, P28)
 *
 * In trajectory mode a left click on the diamond adds this point to the walk as well as reopening
 * it, which is the same "inspect plus an append" a click on a corpus point means. Given a `step`,
 * the card names its place in the walk and marks the residues that differ from its baseline —
 * exactly what `pinnedPointCard` does, in the generated half of the palette.
 *
 * **The `--generated` border stays, for the same reason the pinned card's `--stored` border stays**
 * (P9.5). Membership is said by the highlighted label and the marked residues; the border says
 * where the sequence came from and is not available to say anything else.
 *
 * **The diamond itself is not marked**, and that is a stated gap rather than an oversight: it is
 * the size of a corpus point (2 px at k = 1, `marks.ts`) and there is nothing to draw on it that
 * would be legible. A member whose card is closed is invisible on the map — which is equally true
 * of a stored member whose card was closed — and the panel is where the walk is legible (`V131`).
 */
/**
 * What an interpolated point's card says beyond `generated at (…)` (`V143`, P30.13).
 *
 * **The ordering and the ends, because a diamond cannot carry either.** Three amber diamonds in a
 * row on a UMAP view say nothing about which is step 1 and nothing about what the walk was
 * between; on a PCA view they sit on a straight line and say only that. The clause names the step,
 * the position along the line, and both endpoints in the reader's own words (`originOf`).
 *
 * **`t` is a range when the step stood for a run of duplicates** (`V145`). A point is drawn at the
 * **first** `t` that produced its sequence, so printing that number alone would read as a single
 * sample when it is the start of a stretch that decoded identically.
 */
const runClause = (run: InterpolationRun, at: RunPosition): string => {
  const [lo, hi] = run.tRange;
  const where = lo === hi ? `t = ${lo.toFixed(3)}` : `t = ${lo.toFixed(3)}\u2013${hi.toFixed(3)}`;
  return `step ${at.index} of ${at.total} \u00b7 ${where} \u00b7 ${run.from} \u2192 ${run.to}`;
};

export function decodedMark(
  mark: DecodedMark,
  handlers: { onOpen: () => void; onDelete: () => void; onCloseCard: () => void },
  step?: Step,
  /**
   * Where this point sits in its run, **computed by the caller from the run's current membership**
   * rather than read off the record — see `InterpolationRun.t`. Absent for a point that is not
   * part of a run, and for one whose run the caller could not resolve.
   */
  at?: RunPosition,
  notes?: CardNotes,
): HTMLElement {
  const node = el('div', { class: 'decoded-mark' });

  const glyph = el('button', {
    class: 'decoded-glyph',
    type: 'button',
    title:
      (mark.run === undefined || at === undefined
        ? `generated at ${atLabel(mark.xy)}`
        : `${runClause(mark.run, at)} \u2014 generated at ${atLabel(mark.xy)}`) +
      ' \u2014 not a corpus member. Click to reopen, right-click to delete.',
  });
  glyph.addEventListener('click', (event) => {
    event.stopPropagation();
    handlers.onOpen();
  });
  glyph.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    event.stopPropagation();
    handlers.onDelete();
  });
  node.append(glyph);

  if (mark.card) {
    const close = el(
      'button',
      { class: 'close', type: 'button', title: 'close this card (the point stays)' },
      '\u00d7',
    );
    close.addEventListener('click', (event) => {
      event.stopPropagation();
      handlers.onCloseCard();
    });
    const card = el(
      'div',
      // **On the card, not on `.decoded-mark`** (`V128`). The mark is the zero-size anchor that
      // carries the diamond; dragging it would take the diamond off the coordinate it names.
      { class: 'decoded-point', 'data-drag': '', title: 'drag to reposition' },
      el(
        'p',
        { class: 'label' },
        labelLine(`generated at ${atLabel(mark.xy)} \u2014 not a corpus member`, step),
      ),
      // **A second line rather than a longer first one.** The provenance line is P9.5's sentence
      // and is the same on every generated card; where this point sits in a walk is a different
      // claim, and folding the two would make one unreadable line on a card that is already the
      // widest thing on the map.
      ...(mark.run === undefined || at === undefined
        ? []
        : [el('p', { class: 'label run' }, runClause(mark.run, at))]),
      mark.result.sequence
        ? residueSpans(mark.result.sequence, 'sequence generated', step?.summary?.positions)
        : el('p', { class: 'sequence generated' }, '(empty \u2014 the model emitted EOS first)'),
      close,
      ...notesButton(notes),
    );
    if (step !== undefined) card.classList.add('in-trajectory');
    if (notes?.terse === true) card.classList.add('terse');
    node.append(card);
  }

  // The whole marker sits inside the map's own element, so a click anywhere in it would otherwise
  // reach the map's handler at that pixel.
  node.addEventListener('click', (event) => event.stopPropagation());
  return node;
}

/**
 * The line naming what an encoded point is, in the one place that composes it.
 *
 * **Three clauses, and each is doing work.** The reader's own name first when they gave one,
 * because a bulk import of eighty peptides is unreadable when every card says the same thing.
 * Then the provenance in words — *typed*, placed *by this model* — which is the sentence `V137`
 * requires because the glyph cannot carry it at 2 px. Then the coordinate, which is what the
 * reader will compare against a decoded point beside it.
 */
const encodedLabel = (mark: EncodedMark): (Node | string)[] => {
  const provenance = `typed sequence, placed by this model at ${atLabel(mark.xy)} \u2014 not a corpus member`;
  return mark.label === ''
    ? [provenance]
    : [el('span', { class: 'given-name' }, mark.label), ` \u00b7 ${provenance}`];
};

/**
 * A typed sequence's marker on the map: the triangle, and its card (`V137`, P29.4).
 *
 * `decodedMark`'s twin in the third part of the palette, and every gesture is wired the same way —
 * left click reopens from the record, right click deletes, the card's close leaves the glyph,
 * every click inside stops propagating so it cannot reach the map handler underneath.
 *
 * ## What is different from `decodedMark`, and why
 *
 * **One sequence on the card, and it is the reader's.** Nothing in this app asks the server for a
 * reconstruction (`V140`), so there is no second sequence for P9.6's *beside, never in place of*
 * rule to arbitrate — and, more usefully, the card **cannot imply a round trip it never checked**.
 * When that changes, the reconstruction goes below this line under its own label, which is the
 * shape `showReconstruction` already uses for a stored row.
 *
 * **`nearest corpus row` is on the card rather than only in the pane**, unlike a decoded point's
 * provenance. It is what makes a typed peptide's position *readable*: a coordinate alone says
 * nothing about whether the model thinks this peptide is like anything in the corpus, and
 * "0.0123 from row 41207" says it landed on top of one.
 *
 * **The triangle itself is not marked when the point is a step of a walk**, exactly as the diamond
 * is not: it is the size of a corpus point (2 px at k = 1, `marks.ts`) and there is nothing legible
 * to draw on it. The panel is where a walk is legible (`V131`).
 */
export function encodedMark(
  mark: EncodedMark,
  handlers: { onOpen: () => void; onDelete: () => void; onCloseCard: () => void },
  step?: Step,
  notes?: CardNotes,
): HTMLElement {
  const node = el('div', { class: 'encoded-mark' });

  const glyph = el('button', {
    class: 'encoded-glyph',
    type: 'button',
    title:
      `${mark.label === '' ? 'typed sequence' : mark.label} \u2014 placed at ${atLabel(mark.xy)}, ` +
      'not a corpus member. Click to reopen, right-click to delete.',
  });
  glyph.addEventListener('click', (event) => {
    event.stopPropagation();
    handlers.onOpen();
  });
  glyph.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    event.stopPropagation();
    handlers.onDelete();
  });
  node.append(glyph);

  if (mark.card) {
    const close = el(
      'button',
      { class: 'close', type: 'button', title: 'close this card (the point stays)' },
      '\u00d7',
    );
    close.addEventListener('click', (event) => {
      event.stopPropagation();
      handlers.onCloseCard();
    });
    const card = el(
      'div',
      // **On the card, not on `.encoded-mark`** (`V128`). The mark is the zero-size anchor that
      // carries the triangle; dragging it would take the triangle off the coordinate it names.
      { class: 'encoded-point', 'data-drag': '', title: 'drag to reposition' },
      el('p', { class: 'label' }, ...encodedLabel(mark)),
      residueSpans(mark.sequence, 'sequence typed', step?.summary?.positions),
      el(
        'p',
        { class: 'label' },
        ...(step === undefined ? [] : [stepLabel(step).join(' \u00b7 '), ' \u00b7 ']),
        `nearest corpus row ${mark.nearestRow}, ${mark.nearestDistance.toFixed(4)} away`,
      ),
      close,
      ...notesButton(notes),
    );
    if (step !== undefined) card.classList.add('in-trajectory');
    if (notes?.terse === true) card.classList.add('terse');
    node.append(card);
  }

  node.addEventListener('click', (event) => event.stopPropagation());
  return node;
}

export function createDetail(root: HTMLElement, suffix = ''): Detail {
  let selected: HTMLElement | null = null;

  const provenance = (result: DecodeResponse): HTMLElement =>
    el(
      'dl',
      { class: 'provenance' },
      el('dt', {}, 'fill'),
      el('dd', {}, result.fill),
      el('dt', {}, 'sampler'),
      el('dd', {}, result.sampler),
      el('dt', {}, 'exact'),
      el('dd', {}, exactness(result)),
      el('dt', {}, 'nearest row'),
      el('dd', {}, result.neighbour_row === null ? '—' : String(result.neighbour_row)),
      el('dt', {}, '‖z‖'),
      el('dd', {}, result.z_norm.toFixed(4)),
    );

  function showSelection(datum: PointDatum, onReconstruct: () => void, step?: Step): void {
    const properties = el('dl', { class: 'properties' });
    for (const [key, value] of Object.entries(datum)) {
      if (NOT_A_PROPERTY.has(key)) continue;
      properties.append(el('dt', {}, key), el('dd', {}, String(value)));
    }

    // Suffixed for the second comparison pane (P12.1); empty for pane A, so `#reconstruct` keeps
    // meaning what it meant before side-by-side existed.
    const button = el('button', { id: `reconstruct${suffix}` }, 'show reconstruction');
    button.addEventListener('click', onReconstruct);

    selected = el(
      'section',
      { class: 'card selected' },
      el('h2', {}, 'selected point'),
      // The stored sequence, labelled as stored. `V13`: it came out of the tile, so nothing was
      // fetched to show it.
      el('p', { class: 'label' }, `row ${datum.row} — stored sequence`),
      // **The same trajectory line the map card carries** (`V131`), on its own row rather than
      // appended to the one above: the pane has the width for it, and a selection is a different
      // fact from a step in a walk even when they are the same point.
      ...(step === undefined
        ? []
        : [el('p', { class: 'label trajectory' }, stepLabel(step).join(' · '))]),
      // Per-residue spans rather than one text node (`P18.1`): each code carries `data-residue`
      // and the residue card is one delegated listener above this pane.
      residueSpans(datum.sequence, 'sequence stored', step?.summary?.positions),
      properties,
      button,
    );
    root.replaceChildren(selected);
  }

  function showReconstruction(result: DecodeResponse): void {
    // **Beside, never in place of** (P9.6). The stored sequence stays on screen so the two can be
    // read against each other, which is the only reason to ask for a reconstruction at all.
    const card = el(
      'section',
      { class: 'card generated' },
      el('h2', {}, 'reconstruction'),
      el('p', { class: 'label' }, 'generated by the model from this row’s own stored latent'),
      result.sequence
        ? residueSpans(result.sequence, 'sequence generated')
        : el('p', { class: 'sequence generated' }, '(empty — the model emitted EOS first)'),
      provenance(result),
    );
    if (selected !== null) root.replaceChildren(selected, card);
    else root.replaceChildren(card);
  }

  /**
   * A decoded point in the pane, with its place in the walk when it has one (`V132`).
   *
   * `step` reaches here from two callers — the diamond's own click, and the trajectory panel's
   * ordinal — and produces the same extra line and the same marked residues `showSelection`
   * produces for a corpus row. The provenance list below it is unchanged: which sampler and which
   * fill wrote this sequence is a fact about the sequence, not about the walk.
   */
  function showDecoded(result: DecodeResponse, xy: [number, number], step?: Step): void {
    selected = null;
    root.replaceChildren(
      el(
        'section',
        { class: 'card generated' },
        el('h2', {}, 'decoded'),
        el('p', { class: 'label' }, `generated at ${atLabel(xy)} — not a corpus member`),
        ...(step === undefined
          ? []
          : [el('p', { class: 'label trajectory' }, stepLabel(step).join(' · '))]),
        result.sequence
          ? residueSpans(result.sequence, 'sequence generated', step?.summary?.positions)
          : el('p', { class: 'sequence generated' }, '(empty — the model emitted EOS first)'),
        provenance(result),
      ),
    );
  }

  /**
   * A typed sequence in the pane, with its place in the walk when it has one (`V137`, P29.4).
   *
   * **The card on the map is short and this is not, which is the split every provenance already
   * has here**: `decodedMark`'s card carries the sequence and the pane carries the provenance list
   * under it. What that list holds for an encoded point is what the *model* said about a sequence
   * it did not write — where it put it, what corpus row it landed near, and how far out the
   * latent is.
   *
   * **`‖z‖` is the one figure worth having beside the nearest row.** A typed peptide the model has
   * never seen anything like lands far from the origin of a space fitted on the corpus, and a
   * large norm beside a large `nearest distance` is the signature of exactly that — which is the
   * first thing to know about a sequence somebody invented.
   */
  function showEncoded(mark: EncodedMark, step?: Step): void {
    selected = null;
    root.replaceChildren(
      el(
        'section',
        { class: 'card typed' },
        el('h2', {}, 'typed sequence'),
        el(
          'p',
          { class: 'label' },
          mark.label === ''
            ? 'written by you \u2014 placed by this model, not a corpus member'
            : `${mark.label} \u2014 written by you, placed by this model, not a corpus member`,
        ),
        ...(step === undefined
          ? []
          : [el('p', { class: 'label trajectory' }, stepLabel(step).join(' \u00b7 '))]),
        residueSpans(mark.sequence, 'sequence typed', step?.summary?.positions),
        el(
          'dl',
          { class: 'provenance' },
          el('dt', {}, 'placed at'),
          el('dd', {}, atLabel(mark.xy)),
          el('dt', {}, 'nearest row'),
          el('dd', {}, String(mark.nearestRow)),
          el('dt', {}, 'distance'),
          el('dd', {}, mark.nearestDistance.toFixed(4)),
          el('dt', {}, '\u2016z\u2016'),
          el('dd', {}, mark.zNorm.toFixed(4)),
          el('dt', {}, 'encoded'),
          el('dd', {}, mark.at),
        ),
      ),
    );
  }

  return {
    showSelection,
    showReconstruction,
    showDecoded,
    showEncoded,
    showError: (message) =>
      root.replaceChildren(el('section', { class: 'card error' }, el('h2', {}, 'refused'), el('p', {}, message))),
    busy: (what) => root.replaceChildren(el('section', { class: 'card busy' }, el('p', {}, what))),
    clear: () => {
      selected = null;
      root.replaceChildren();
    },
  };
}
